/**
 * Helper functions for ServiceabilityInfoStep component
 */
import { message } from 'antd';
import {
	API_ERROR_LOG_MESSAGE,
	ROUTE_REMOVED_SUCCESS_MESSAGE,
	ROUTE_UPDATED_SUCCESS_MESSAGE,
} from './ServiceabilityInfoStep.constants';

/**
 * Validate route fields
 */
export const validateRouteFields = (currentRoute) => {
	if (
		!currentRoute.type_of_movement ||
		!currentRoute.source_city ||
		!currentRoute.destination_city
	) {
		message.error(
			'Please fill in all route fields (Type of Movement, Source City, Destination City)'
		);
		return false;
	}
	return true;
};

/**
 * Validate editing route fields
 */
export const validateEditingRoute = (editingRoute) => {
	if (!editingRoute.typeOfMovement || !editingRoute.sourceCity || !editingRoute.destinationCity) {
		message.error('Please fill in all route fields');
		return false;
	}
	return true;
};

/**
 * Build route object from API response
 */
export const buildRouteFromResponse = (responseData, currentRoute) => ({
	key: responseData?.id?.toString() || Date.now().toString(),
	id: responseData?.id,
	vendorName: responseData?.vendor_name || currentRoute.vendor_name,
	typeOfMovement: responseData?.type_of_movement || currentRoute.type_of_movement,
	sourceCity: responseData?.source_city || currentRoute.source_city,
	destinationCity: responseData?.destination_city || currentRoute.destination_city,
});

/**
 * Build route object for create mode
 */
export const buildRouteForCreate = (currentRoute) => ({
	key: Date.now().toString(),
	vendorName: currentRoute.vendor_name,
	typeOfMovement: currentRoute.type_of_movement,
	sourceCity: currentRoute.source_city,
	destinationCity: currentRoute.destination_city,
});

/**
 * Handle API response for adding route
 */
export const handleAPIResponse = (response, routes, setFieldValue, currentRoute) => {
	if (response && response.status === 'success') {
		const newRoute = buildRouteFromResponse(response.data, currentRoute);
		const updatedRoutes = [...routes, newRoute];
		setFieldValue('routes', updatedRoutes);
		return true;
	}
	const errorMessage = response?.error?.message || response?.message || 'Failed to add route';
	message.error(errorMessage);
	return false;
};

/**
 * Update route in local state
 */
export const updateRouteInLocalState = (
	routes,
	selectedRoute,
	editingRoute,
	setFieldValue,
	handleModalClose
) => {
	const updatedRoutes = routes.map((route) =>
		route.key === selectedRoute.key || route.id === selectedRoute.id
			? {
					...route,
					typeOfMovement: editingRoute.typeOfMovement,
					sourceCity: editingRoute.sourceCity,
					destinationCity: editingRoute.destinationCity,
			  }
			: route
	);
	setFieldValue('routes', updatedRoutes);
	message.success(ROUTE_UPDATED_SUCCESS_MESSAGE);
	handleModalClose();
};

/**
 * Handle API response for updating route
 */
export const handleUpdateRouteResponse = (
	response,
	routes,
	selectedRoute,
	editingRoute,
	setFieldValue,
	handleModalClose
) => {
	if (response && response.status === 'success') {
		updateRouteInLocalState(
			routes,
			selectedRoute,
			editingRoute,
			setFieldValue,
			handleModalClose
		);
		return true;
	}
	const errorMessage = response?.error?.message || response?.message || 'Failed to update route';
	message.error(errorMessage);
	return false;
};

/**
 * Remove route from local state
 */
export const removeRouteFromLocalState = (
	routes,
	selectedRoute,
	setFieldValue,
	handleModalClose
) => {
	const updatedRoutes = routes.filter(
		(route) =>
			route.key !== selectedRoute.key && (!selectedRoute.id || route.id !== selectedRoute.id)
	);
	setFieldValue('routes', updatedRoutes);
	message.success(ROUTE_REMOVED_SUCCESS_MESSAGE);
	handleModalClose();
};

/**
 * Handle API response for removing route
 */
export const handleRemoveRouteResponse = (
	response,
	routes,
	selectedRoute,
	setFieldValue,
	handleModalClose
) => {
	if (response && response.status === 'success') {
		removeRouteFromLocalState(routes, selectedRoute, setFieldValue, handleModalClose);
		return true;
	}
	const errorMessage = response?.error?.message || response?.message || 'Failed to remove route';
	message.error(errorMessage);
	return false;
};

/**
 * Get error handler for API calls
 */
export const getErrorHandler = () => (errors) => {
	console.error(API_ERROR_LOG_MESSAGE, errors);
};
